/**
 * Runs a call flow. The same code answers the live call (the API, one step at
 * a time between the dialplan's actions) and the editor's simulator, so what
 * the simulator shows is what calls do.
 *
 * A walk goes from a way out through the elements that need no telephony
 * (opening hours, caller lists, counters...) to the next one that does
 * (play, menu, queue, transfer, voicemail, hang up), which the caller's
 * channel then carries out. Its result (the key pressed, how the queue
 * ended...) picks the way out the next walk starts from.
 */
/** A node visited more often than this in one call, or more steps than MAX_STEPS: the call ends (with a goodbye). */
export const MAX_VISITS = 5;
export const MAX_STEPS = 100;
export const DEFAULT_MENU_TIMEOUT_SECONDS = 5;
export const DEFAULT_TRANSFER_RING_SECONDS = 30;
export function startRun(versionId, graph, language) {
    return { versionId, graph, language, visits: new Map(), steps: 0 };
}
const name = (env, kind, id) => (id && env.names?.(kind, id)) || (id ? 'unknown' : 'none');
/** The way out a telephony element's result leads to. */
export function exitFor(node, result) {
    switch (node.type) {
        case 'menu':
            if (result === '')
                return 'timeout';
            return (node.data.keys ?? []).includes(result) ? `key:${result}` : 'invalid';
        case 'queue':
            return ['answered', 'timeout', 'nobody', 'callback'].includes(result) ? result : 'nobody';
        case 'transfer':
            return result === 'answered' ? 'answered' : 'noanswer';
        default:
            return 'next';
    }
}
/**
 * From a way out (or the start, for null) to the next element that needs
 * the caller's channel. Updates the run (visits, language, the flow it is in).
 */
export function walk(run, from, env) {
    const steps = [];
    const effects = [];
    const nodeById = (id) => run.graph.nodes.find((n) => n.id === id) ?? null;
    const target = (nodeId, exit) => {
        const edge = run.graph.edges.find((e) => e.source === nodeId && e.sourceHandle === exit);
        return edge ? nodeById(edge.target) : null;
    };
    const record = (node, exit, note, detail = null) => steps.push({ versionId: run.versionId, nodeId: node.id, type: node.type, exit, note, detail });
    let node = from ? target(from.nodeId, from.exit) : (run.graph.nodes.find((n) => n.type === 'start') ?? null);
    for (;;) {
        if (!node)
            return { steps, effects, action: { kind: 'hangup', nodeId: null, why: 'dead-end' } };
        const key = `${run.versionId}:${node.id}`;
        const visits = (run.visits.get(key) ?? 0) + 1;
        run.visits.set(key, visits);
        run.steps += 1;
        if (visits > MAX_VISITS || run.steps > MAX_STEPS) {
            record(node, null, 'Too many times round: goodbye');
            return { steps, effects, action: { kind: 'hangup', nodeId: node.id, why: 'loop' } };
        }
        const d = node.data;
        switch (node.type) {
            case 'start':
                record(node, 'next', 'Call comes in');
                node = target(node.id, 'next');
                continue;
            case 'counter':
                record(node, 'next', `Counted${d.label ? `: ${d.label}` : ''}`);
                node = target(node.id, 'next');
                continue;
            case 'language':
                run.language = d.language ?? run.language;
                record(node, 'next', `Language: ${run.language}`, run.language);
                node = target(node.id, 'next');
                continue;
            case 'hours': {
                const hours = d.hoursId ? env.hours.get(d.hoursId) : undefined;
                const state = hours ? hoursAt(hours, env.at, env.timeZone) : { exit: 'closed', note: 'Opening hours missing: closed' };
                // A period added to the hours after publishing has no way out yet: closed.
                const exit = state.exit.startsWith('period:') && !target(node.id, state.exit) ? 'closed' : state.exit;
                record(node, exit, `${name(env, 'hours', d.hoursId)}: ${state.note}`, state.exit);
                node = target(node.id, exit);
                continue;
            }
            case 'callerList': {
                const on = Boolean(d.listId && env.lists.get(d.listId)?.has(env.caller));
                record(node, on ? 'on' : 'off', `${env.caller} ${on ? 'is' : 'is not'} on ${name(env, 'list', d.listId)}`);
                node = target(node.id, on ? 'on' : 'off');
                continue;
            }
            case 'callback':
                if (d.queueId)
                    effects.push({ kind: 'callback', nodeId: node.id, queueId: d.queueId, email: d.email ?? null });
                record(node, 'next', `Callback task for ${name(env, 'queue', d.queueId)}${d.email ? `, emailed to ${d.email}` : ''}`);
                node = target(node.id, 'next');
                continue;
            case 'gotoFlow': {
                const flow = d.flowId ? env.flows.get(d.flowId) : undefined;
                record(node, null, `Go to ${name(env, 'flow', d.flowId)}`);
                if (!flow)
                    return { steps, effects, action: { kind: 'hangup', nodeId: node.id, why: 'dead-end' } };
                run.versionId = flow.versionId;
                run.graph = flow.graph;
                node = flow.graph.nodes.find((n) => n.type === 'start') ?? null;
                continue;
            }
            case 'play':
                record(node, null, `Play ${name(env, 'prompt', d.promptId)}`);
                return { steps, effects, action: { kind: 'play', nodeId: node.id, promptId: d.promptId ?? '' } };
            case 'menu':
                record(node, null, `Menu ${name(env, 'prompt', d.promptId)}`);
                return {
                    steps,
                    effects,
                    action: { kind: 'menu', nodeId: node.id, promptId: d.promptId ?? '', keys: d.keys ?? [], timeoutSeconds: d.timeoutSeconds ?? DEFAULT_MENU_TIMEOUT_SECONDS },
                };
            case 'queue':
                record(node, null, `Queue ${name(env, 'queue', d.queueId)}`);
                return { steps, effects, action: { kind: 'queue', nodeId: node.id, queueId: d.queueId ?? '', continueAfter: Boolean(target(node.id, 'answered')) } };
            case 'transfer': {
                const to = d.transferTo === 'number' ? 'number' : 'user';
                record(node, null, `Transfer to ${to === 'number' ? (d.number ?? 'none') : name(env, 'user', d.userId)}`);
                return {
                    steps,
                    effects,
                    action: {
                        kind: 'transfer',
                        nodeId: node.id,
                        to,
                        userId: to === 'user' ? (d.userId ?? null) : null,
                        number: to === 'number' ? (d.number ?? null) : null,
                        ringSeconds: d.ringSeconds ?? DEFAULT_TRANSFER_RING_SECONDS,
                        continueAfter: Boolean(target(node.id, 'answered')),
                    },
                };
            }
            case 'voicemail': {
                const forQueue = d.voicemailFor !== 'user';
                record(node, null, `Voicemail for ${forQueue ? name(env, 'queue', d.queueId) : name(env, 'user', d.userId)}`);
                return {
                    steps,
                    effects,
                    action: {
                        kind: 'voicemail',
                        nodeId: node.id,
                        for: forQueue ? 'queue' : 'user',
                        queueId: forQueue ? (d.queueId ?? null) : null,
                        userId: forQueue ? null : (d.userId ?? null),
                        promptId: d.promptId ?? null,
                        email: d.email ?? null,
                    },
                };
            }
            case 'hangup':
                record(node, null, 'Hang up');
                return { steps, effects, action: { kind: 'hangup', nodeId: node.id, why: 'hangup' } };
        }
    }
}
// ---- Opening hours ----
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const minutesOf = (time) => Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));
const isOpen = (kind) => kind.trim().toLowerCase() === 'open';
/** The local date, weekday (1 Monday ... 7 Sunday) and minute of the day in a time zone. */
export function localTime(at, timeZone) {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        weekday: 'short',
    }).formatToParts(at);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
    return {
        date: `${get('year')}-${get('month')}-${get('day')}`,
        weekday: WEEKDAYS.indexOf(get('weekday')) + 1,
        minutes: Number(get('hour')) * 60 + Number(get('minute')),
    };
}
const previousDate = (date) => {
    const [y, m, d] = date.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d - 1)).toISOString().slice(0, 10);
};
/** The named periods of a schedule (its ways out besides open and closed), in order of appearance. */
export function hoursPeriods(hours) {
    const kinds = [...hours.weekly.map((p) => p.kind), ...hours.exceptions.flatMap((e) => e.periods.map((p) => p.kind))].map((k) => k.trim());
    return [...new Set(kinds.filter((k) => k && !isOpen(k)))];
}
/**
 * Open, closed, or a named period, at an instant in a time zone. A day in an
 * exception (holiday, closure, special hours) uses the exception's periods
 * instead of the week's; no periods means closed all day. A period ending at
 * or before it starts runs past midnight (22:00–06:00); 00:00–00:00 is all
 * day. A named period (lunch) wins over open hours it overlaps.
 */
export function hoursAt(hours, at, timeZone) {
    const now = localTime(at, timeZone);
    const periodsOn = (date, weekday) => {
        const exception = hours.exceptions.find((e) => e.from <= date && date <= e.to);
        return exception ? { periods: exception.periods, label: exception.label || 'Exception' } : { periods: hours.weekly.filter((p) => p.day === weekday), label: null };
    };
    const today = periodsOn(now.date, now.weekday);
    const yesterday = periodsOn(previousDate(now.date), now.weekday === 1 ? 7 : now.weekday - 1);
    const matching = [
        ...today.periods.filter((p) => {
            const from = minutesOf(p.from);
            const to = minutesOf(p.to);
            return now.minutes >= from && (to > from ? now.minutes < to : true);
        }),
        // A period of yesterday's that runs past midnight.
        ...yesterday.periods.filter((p) => minutesOf(p.to) <= minutesOf(p.from) && now.minutes < minutesOf(p.to)),
    ];
    const chosen = matching.find((p) => !isOpen(p.kind)) ?? matching.find((p) => isOpen(p.kind));
    const exit = chosen ? (isOpen(chosen.kind) ? 'open' : `period:${chosen.kind.trim()}`) : 'closed';
    const clock = `${String(Math.floor(now.minutes / 60)).padStart(2, '0')}:${String(now.minutes % 60).padStart(2, '0')}`;
    const state = exit === 'open' ? 'Open' : exit === 'closed' ? 'Closed' : chosen.kind.trim();
    return { exit, note: `${state} (${WEEKDAYS[now.weekday - 1]} ${now.date} ${clock}${today.label ? `, ${today.label}` : ''})` };
}
/**
 * The path a call would take: the same walk as a live call, with the keys
 * and outcomes given (keys in order, one per menu reached; queues and
 * transfers answered unless said otherwise).
 */
export function simulateFlow(versionId, graph, language, env, input) {
    const run = startRun(versionId, graph, language);
    const steps = [];
    let keyIndex = 0;
    let from = null;
    const finish = (outcome) => ({ path: steps.map(({ detail: _, ...step }) => step), steps, outcome });
    for (;;) {
        const result = walk(run, from, env);
        steps.push(...result.steps);
        const last = steps.at(-1);
        const { action } = result;
        const resolve = (exit, note, detail = null) => {
            if (last)
                Object.assign(last, { exit, note: `${last.note}: ${note}`, detail });
            from = { nodeId: action.nodeId, exit };
        };
        switch (action.kind) {
            case 'play':
                resolve('next', 'played');
                continue;
            case 'menu': {
                const key = input.keys[keyIndex++] ?? '';
                const node = run.graph.nodes.find((n) => n.id === action.nodeId);
                const exit = exitFor(node, key);
                resolve(exit, key === '' ? 'no key pressed' : exit === 'invalid' ? `pressed ${key} (not a choice)` : `pressed ${key}`, key);
                continue;
            }
            case 'queue': {
                const outcome = input.queueOutcomes[action.nodeId] ?? 'answered';
                const label = { answered: 'answered', timeout: 'waited too long', nobody: 'nobody there', callback: 'asked for a callback' }[outcome];
                if (outcome === 'answered' && !action.continueAfter) {
                    if (last)
                        Object.assign(last, { exit: 'answered', note: `${last.note}: answered` });
                    return finish(`Answered in ${name(env, 'queue', action.queueId)}`);
                }
                resolve(outcome, label);
                continue;
            }
            case 'transfer': {
                const outcome = input.transferOutcomes[action.nodeId] ?? 'answered';
                const who = action.to === 'number' ? (action.number ?? '') : name(env, 'user', action.userId);
                if (outcome === 'answered' && !action.continueAfter) {
                    if (last)
                        Object.assign(last, { exit: 'answered', note: `${last.note}: answered` });
                    return finish(`Answered by ${who}`);
                }
                resolve(outcome, outcome === 'answered' ? 'answered' : 'not answered');
                continue;
            }
            case 'voicemail':
                return finish(`Leaves a message for ${action.for === 'queue' ? name(env, 'queue', action.queueId) : name(env, 'user', action.userId)}`);
            case 'hangup':
                return finish(action.why === 'loop' ? 'Hung up: went round too many times' : action.why === 'dead-end' ? 'Hung up: a way out leads nowhere' : 'Hung up');
        }
    }
}
